import React, { useCallback, useMemo, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { FaSearch, FaGripVertical, FaUserCheck, FaUserSlash } from 'react-icons/fa';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners
} from '@dnd-kit/core';

const getUserDisplayName = (user) =>
  `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;

const UserCard = ({ user, dragging = false }) => (
  <div
    className={`flex items-center gap-2 p-2 mb-2 rounded-lg border bg-white shadow-sm transition-all ${dragging ? 'border-purple-400 shadow-lg rotate-1' : 'border-gray-200 hover:border-purple-300 hover:shadow'}`}
  >
    <FaGripVertical className="w-4 h-4 text-gray-400 shrink-0" />
    <div className="min-w-0">
      <div className="font-medium text-gray-800 text-sm truncate">{getUserDisplayName(user)}</div>
      <div className="text-xs text-gray-500 truncate">{user.email}</div>
    </div>
  </div>
);

const DraggableUser = ({ user, from }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `user-${user.id}`,
    data: { user, from }
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40' : ''}`}
    >
      <UserCard user={user} />
    </div>
  );
};

const DroppableColumn = ({ id, title, icon, accent, users }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div className="flex-1 min-w-0">
      <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl text-white font-semibold ${accent}`}>
        <span className="flex items-center gap-2">{icon}{title}</span>
        <span className="bg-white/30 rounded-full px-2 py-0.5 text-xs">{users.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={`min-h-[320px] max-h-[60vh] overflow-y-auto p-3 rounded-b-xl border-2 border-t-0 transition-colors ${isOver ? 'border-purple-400 bg-purple-50/60' : 'border-gray-200 bg-gray-50/60'}`}
      >
        {users.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-10">Drag users here</div>
        ) : (
          users.map((user) => <DraggableUser key={user.id} user={user} from={id} />)
        )}
      </div>
    </div>
  );
};

const GroupAssignBoard = ({ assigned, available, setAssigned, setAvailable }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeUser, setActiveUser] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const filterUsers = useCallback((users) => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.trim().toLowerCase();
    return users.filter((user) =>
      getUserDisplayName(user).toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  const filteredAvailable = useMemo(() => filterUsers(available), [available, filterUsers]);
  const filteredAssigned = useMemo(() => filterUsers(assigned), [assigned, filterUsers]);

  const handleDragStart = (event) => {
    setActiveUser(event.active.data.current?.user || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveUser(null);
    if (!over) return;

    const from = active.data.current?.from;
    const to = over.id;
    const user = active.data.current?.user;
    if (!user || !from || from === to) return;

    if (to === 'assigned') {
      setAvailable((prev) => prev.filter((u) => u.id !== user.id));
      setAssigned((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
      return;
    }
    if (to === 'available') {
      setAssigned((prev) => prev.filter((u) => u.id !== user.id));
      setAvailable((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
    }
  };

  return (
    <div>
      <div className="mb-4 max-w-md">
        <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
          <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
            <FaSearch className="w-5 h-5" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 focus:ring-2 focus:ring-purple-500 text-sm"
          />
        </InputGroup>
      </div>

      <p className="text-sm text-gray-500 mb-3">Drag and drop users between the columns, then save.</p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-col md:flex-row gap-4">
          <DroppableColumn
            id="available"
            title="Unassigned Users"
            icon={<FaUserSlash className="w-4 h-4" />}
            accent="bg-gradient-to-r from-gray-500 to-gray-700"
            users={filteredAvailable}
          />
          <DroppableColumn
            id="assigned"
            title="Assigned Users"
            icon={<FaUserCheck className="w-4 h-4" />}
            accent="bg-gradient-to-r from-purple-600 to-pink-600"
            users={filteredAssigned}
          />
        </div>

        <DragOverlay>
          {activeUser ? (
            <div className="w-64">
              <UserCard user={activeUser} dragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default GroupAssignBoard;
